(function () {
  'use strict';

  if (customElements.get('yatta-video-carousel')) return;

  var MOBILE_QUERY = '(max-width: 768px)';
  var ACTIVE_THRESHOLD = 0.6;

  class YattaVideoCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(
        this.querySelectorAll('.yatta-video-carousel__video-wrap')
      );

      this.slides.forEach(function (wrap) {
        var video = wrap.querySelector('video');
        if (!video) return;

        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.disablePictureInPicture = true;

        var playBtn = wrap.querySelector('[data-yatta-vc-play-btn]');
        if (playBtn) {
          playBtn.addEventListener('click', this.handleTogglePlay.bind(this, video));
        }

        var muteBtn = wrap.querySelector('[data-yatta-vc-mute-btn]');
        if (muteBtn) {
          muteBtn.addEventListener('click', this.handleToggleMute.bind(this, video));
        }

        video.addEventListener('play', function () {
          wrap.classList.add('is-playing');
        });

        video.addEventListener('pause', function () {
          wrap.classList.remove('is-playing');
        });

        video.addEventListener('ended', function () {
          wrap.classList.remove('is-playing');
        });

        // Fires for both user-driven and programmatic mute changes, so the
        // icon and the wrap's is-muted class always reflect the video's
        // real state regardless of what triggered the change.
        video.addEventListener('volumechange', function () {
          wrap.classList.toggle('is-muted', video.muted);
          if (muteBtn) {
            muteBtn.setAttribute('aria-pressed', String(video.muted));
            muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
          }
        });
      }, this);

      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.autoplayEnabled = this.getAttribute('data-autoplay-mobile') === 'true';

      this.updateMode();
      this.mobileQuery.addEventListener
        ? this.mobileQuery.addEventListener('change', this.updateMode.bind(this))
        : this.mobileQuery.addListener(this.updateMode.bind(this));
    }

    updateMode() {
      var isMobile = this.mobileQuery.matches;

      if (isMobile && this.autoplayEnabled) {
        this.enableSwipeAutoplay();
      } else {
        this.disableSwipeAutoplay();
        this.pauseAll();
      }
    }

    enableSwipeAutoplay() {
      if (this.observer) return;

      // root: null -> the actual browser viewport, not the horizontally-
      // scrolling track. Using the track as root meant a slide could read as
      // "fully visible" purely from its position in the carousel strip, even
      // while the whole section was still off-screen below the fold, which
      // is why autoplay wasn't reliably kicking in on first view.
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        { root: null, threshold: [0, ACTIVE_THRESHOLD, 1] }
      );

      this.slides.forEach(function (wrap) {
        this.observer.observe(wrap);
      }, this);
    }

    disableSwipeAutoplay() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
    }

    handleIntersection(entries) {
      entries.forEach(function (entry) {
        var video = entry.target.querySelector('video');
        if (!video) return;

        if (entry.intersectionRatio >= ACTIVE_THRESHOLD) {
          // Muted state is intentionally left as-is here (not forced back
          // to true): the element already starts muted via the HTML
          // attribute, which satisfies the browser's autoplay-with-sound
          // gesture requirement on first play. If the visitor has since
          // unmuted it via the speaker button (a real gesture), re-forcing
          // muted = true on every re-entry into view would undo their
          // choice each time they scroll away and back.
          video.loop = true;
          video.play().catch(function () {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      });
    }

    // Center overlay toggles play/pause only — muting is handled
    // independently by handleToggleMute. No native <video controls> are
    // ever turned on — that's what brings the scrubber, skip-10s buttons,
    // fullscreen, and Picture-in-Picture along with it, none of which are
    // wanted here. Play/pause state is shown purely via the .is-playing
    // class (see CSS), which fades the custom play icon in and out.
    handleTogglePlay(video) {
      if (video.paused) {
        this.pauseAll();
        video.play().catch(function () {});
      } else {
        video.pause();
      }
    }

    // A click on the speaker icon is itself a direct user gesture, so the
    // browser allows unmuting an already-playing (autoplay-muted) video in
    // place — no pause/replay dance required.
    handleToggleMute(video) {
      video.muted = !video.muted;
    }

    pauseAll() {
      this.slides.forEach(function (wrap) {
        var video = wrap.querySelector('video');
        if (video && !video.paused) video.pause();
      });
    }
  }

  customElements.define('yatta-video-carousel', YattaVideoCarousel);
})();
